import React, { useEffect, useState } from "react";
import { Modal, Spin, message } from "antd";
import { CheckOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { stripePromise } from "../../../services/stripe";
import {
  getDefaultCardId,
  getTokenBalance,
  getTokenPacks,
  startTopup,
} from "../subscription.service";
import { TokenBalance, TokenPack } from "../subscription.types";

interface BuyTokensModalProps {
  open: boolean;
  onClose: () => void;
  onBought: (balance: TokenBalance) => void;
}

const BuyTokensInner: React.FC<Omit<BuyTokensModalProps, "open">> = ({
  onClose,
  onBought,
}) => {
  const { t } = useTranslation("subscription");
  const stripe = useStripe();
  const [packs, setPacks] = useState<TokenPack[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  useEffect(() => {
    let active = true;
    getTokenPacks()
      .then((p) => {
        if (active) {
          setPacks(p);
          setSelected(p[0]?.id ?? null);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Stable per pack selection — reused across a retry of the same purchase
  // (e.g. the network drops after the charge succeeded) so it can't be
  // double-charged; a fresh key is drawn whenever the selected pack changes.
  useEffect(() => {
    if (selected) setIdempotencyKey(`topup:${selected}:${crypto.randomUUID()}`);
  }, [selected]);

  const perToken = (pack: TokenPack) => (pack.price / pack.tokens).toFixed(3);

  const handleBuy = async () => {
    if (!selected) return;
    setBuying(true);
    try {
      const res = await startTopup(selected, idempotencyKey);

      // Dev / no-Stripe: credits added instantly.
      if (res.mode === "record_only") {
        onBought(await getTokenBalance());
        return;
      }

      // Stripe: confirm the PaymentIntent with the saved default card.
      if (!stripe || !res.clientSecret) {
        message.error(t("buyTokens.errors.paymentUnavailable"));
        return;
      }
      const pmId = await getDefaultCardId();
      if (!pmId) {
        message.error(t("buyTokens.errors.addCardFirst"));
        return;
      }
      const result = await stripe.confirmCardPayment(res.clientSecret, {
        payment_method: pmId,
      });
      if (result.error) {
        message.error(result.error.message || t("buyTokens.errors.paymentFailed"));
        return;
      }
      // Credits are granted by the webhook — refresh the balance shortly after.
      await new Promise((r) => setTimeout(r, 2000));
      onBought(await getTokenBalance());
    } catch (e: any) {
      message.error(e?.response?.data?.detail || t("buyTokens.errors.purchaseFailed"));
    } finally {
      setBuying(false);
    }
  };

  return (
    <>
      <h3 className="sub-modal-title">{t("buyTokens.title")}</h3>
      <p className="sub-modal-sub">{t("buyTokens.subtitle")}</p>

      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          <div className="sub-pack-list">
            {packs.map((pack) => (
              <button
                key={pack.id}
                className={`sub-pack${selected === pack.id ? " selected" : ""}`}
                onClick={() => setSelected(pack.id)}
              >
                <span className="sub-pack-check">
                  {selected === pack.id && <CheckOutlined />}
                </span>
                <span className="sub-pack-main">
                  <span className="sub-pack-tokens">
                    {pack.tokens.toLocaleString()} {t("buyTokens.creditsSuffix")}
                  </span>
                  <span className="sub-pack-per">
                    ${perToken(pack)}
                    {t("buyTokens.perCreditSuffix")}
                  </span>
                </span>
                <span className="sub-pack-right">
                  <span className="sub-pack-price">${pack.price}</span>
                  {pack.discountLabel && (
                    <span className="sub-pack-badge">{pack.discountLabel}</span>
                  )}
                </span>
              </button>
            ))}
          </div>

          <div className="sub-note">
            <InfoCircleOutlined className="ic" />
            <span>{t("buyTokens.note")}</span>
          </div>

          <div className="sub-modal-actions">
            <button className="sub-btn sub-btn-outline" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              className="sub-btn sub-btn-primary"
              disabled={buying || !selected}
              onClick={handleBuy}
            >
              {buying ? t("buyTokens.buying") : t("buyTokens.buy")}
            </button>
          </div>
        </>
      )}
    </>
  );
};

const BuyTokensModal: React.FC<BuyTokensModalProps> = ({
  open,
  onClose,
  onBought,
}) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    centered
    width={460}
    className="sub-modal"
    destroyOnClose
  >
    <Elements stripe={stripePromise}>
      <BuyTokensInner onClose={onClose} onBought={onBought} />
    </Elements>
  </Modal>
);

export default BuyTokensModal;
